import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import {
  IsReportReviewedResponse,
  OrderReportResponse,
  ReanalysisCountResponse,
  ReanalysisDecision,
  ReanalysisSignals,
  ReportCriticalityResponse,
} from '@/types/OrderReportTypes'
import * as Sentry from '@sentry/react'

/**
 * Fetches all reports for an order from the API
 *
 * @param orderId - The order ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @returns Promise with array of report metadata
 * @throws Error if the request fails
 */
export async function getAllOrderReports(orderId: string, caller: string): Promise<OrderReportResponse[]> {
  return Sentry.startSpan(
    {
      op: 'report.list',
      name: 'Fetch All Order Reports',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(`/orders/${orderId}/reports?caller=${encodeURIComponent(caller)}`)

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to fetch reports: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.list', orderId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      const reports = await response.json()
      span.setAttribute('reports.count', Array.isArray(reports) ? reports.length : 0)
      return Array.isArray(reports) ? reports : []
    }
  )
}

/**
 * Fetches a specific order report PDF from the API by report ID
 *
 * @param orderId - The order ID
 * @param reportId - The report ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @returns Promise with the PDF bytes as Uint8Array
 * @throws Error if the request fails or response is not a PDF
 */
export async function getOrderReportById(orderId: string, reportId: string, caller: string): Promise<Uint8Array> {
  return Sentry.startSpan(
    {
      op: 'report.fetch',
      name: 'Fetch Order Report PDF by ID',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)
      span.setAttribute('report.id', reportId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(`/orders/${orderId}/reports/${reportId}?caller=${encodeURIComponent(caller)}`)

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to fetch report: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId, reportId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/pdf')) {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'Response is not a PDF file')
        const error = new Error('Response is not a PDF file')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId, reportId },
          contexts: { response: { contentType } },
        })
        throw error
      }

      const arrayBuffer = await response.arrayBuffer()

      // Validate that it's actually PDF data (PDF files start with %PDF)
      const bytes = new Uint8Array(arrayBuffer)
      if (bytes.length < 4) {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'File is too small to be a valid PDF')
        const error = new Error('File is too small to be a valid PDF')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId, reportId },
          contexts: { file: { size: bytes.length } },
        })
        throw error
      }

      const header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3])
      if (header !== '%PDF') {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'File does not appear to be a valid PDF')
        const error = new Error('File does not appear to be a valid PDF')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId, reportId },
          contexts: { file: { header, size: bytes.length } },
        })
        throw error
      }

      span.setAttribute('pdf.size', bytes.length)
      return bytes
    }
  )
}

/**
 * Fetches an order report PDF from the API (latest report)
 *
 * @param orderId - The order ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @returns Promise with the PDF bytes as Uint8Array
 * @throws Error if the request fails or response is not a PDF
 */
export async function getOrderReport(orderId: string, caller: string): Promise<Uint8Array> {
  return Sentry.startSpan(
    {
      op: 'report.fetch',
      name: 'Fetch Order Report PDF',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(`/orders/${orderId}/report/?caller=${encodeURIComponent(caller)}`)

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to fetch report: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      // Check if response is actually a PDF
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/pdf')) {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'Response is not a PDF file')
        const error = new Error('Response is not a PDF file')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId },
          contexts: { response: { contentType } },
        })
        throw error
      }

      const arrayBuffer = await response.arrayBuffer()

      // Validate that it's actually PDF data (PDF files start with %PDF)
      const bytes = new Uint8Array(arrayBuffer)
      if (bytes.length < 4) {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'File is too small to be a valid PDF')
        const error = new Error('File is too small to be a valid PDF')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId },
          contexts: { file: { size: bytes.length } },
        })
        throw error
      }

      const header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3])
      if (header !== '%PDF') {
        span.setAttribute('error', true)
        span.setAttribute('error.message', 'File does not appear to be a valid PDF')
        const error = new Error('File does not appear to be a valid PDF')
        Sentry.captureException(error, {
          tags: { operation: 'report.fetch', orderId },
          contexts: { file: { header, size: bytes.length } },
        })
        throw error
      }

      span.setAttribute('pdf.size', bytes.length)
      return bytes
    }
  )
}

/**
 * Checks whether a report has been reviewed (GET orders/{order_id}/reports/{report_id}/reviewed).
 *
 * @param orderId - The order ID
 * @param reportId - The report ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @returns Promise with reviewed status
 * @throws Error if the request fails
 */
export async function getReportReviewed(
  orderId: string,
  reportId: string,
  caller: string
): Promise<IsReportReviewedResponse> {
  return Sentry.startSpan(
    {
      op: 'report.reviewed.get',
      name: 'Get Report Reviewed Status',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)
      span.setAttribute('report.id', reportId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(
        `/orders/${orderId}/reports/${reportId}/reviewed?caller=${encodeURIComponent(caller)}`
      )

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to get report reviewed status: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.reviewed.get', orderId, reportId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      return response.json()
    }
  )
}

/**
 * Marks a report as reviewed (PATCH orders/{order_id}/reports/{report_id}/reviewed).
 *
 * @param orderId - The order ID
 * @param reportId - The report ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @param reviewerId - Optional practitioner UUID of the reviewer
 * @throws Error if the request fails
 */
export async function markReportReviewed(
  orderId: string,
  reportId: string,
  caller: string,
  reviewerId?: string | null
): Promise<void> {
  return Sentry.startSpan(
    {
      op: 'report.reviewed.patch',
      name: 'Mark Report Reviewed',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)
      span.setAttribute('report.id', reportId)
      if (reviewerId) span.setAttribute('reviewer.id', reviewerId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.patch(
        `/orders/${orderId}/reports/${reportId}/reviewed?caller=${encodeURIComponent(caller)}`,
        { reviewer_id: reviewerId ?? null },
        { 'Content-Type': 'application/json' }
      )

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to mark report reviewed: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.reviewed.patch', orderId, reportId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }
    }
  )
}

/**
 * Fetches the criticality of a report (GET orders/{order_id}/reports/{report_id}/criticality).
 *
 * @param orderId - The order ID
 * @param reportId - The report ID
 * @param caller - The caller identifier (defaults to 'spa')
 * @returns Promise with report criticality
 * @throws Error if the request fails
 */
export async function getReportCriticality(
  orderId: string,
  reportId: string,
  caller: string
): Promise<ReportCriticalityResponse> {
  return Sentry.startSpan(
    {
      op: 'report.criticality.get',
      name: 'Get Report Criticality',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)
      span.setAttribute('report.id', reportId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(
        `/orders/${orderId}/reports/${reportId}/criticality?caller=${encodeURIComponent(caller)}`
      )

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to get report criticality: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.criticality.get', orderId, reportId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      return response.json()
    }
  )
}

/**
 * Fetches the order's current reanalysis count (GET /orders/{order_id}/reanalysis-count).
 *
 * The count is ``completed_reanalysis_reports + (reanalysis_in_flight ? 1 : 0)``.
 * Pair it with the tenant's ``wetLab.reanalysisLimit`` config (from /config/ui)
 * via :func:`shouldBlockNewReanalysis` to decide whether to disable requesting a
 * new reanalysis.
 *
 * @param orderId - The order ID (UUID)
 * @param caller - The caller identifier (e.g. 'spa')
 * @param signal - Optional AbortSignal to cancel superseded requests
 * @returns Promise with the reanalysis count payload
 * @throws Error if the request fails (401/403/404/422/500). A 404/422 means the
 *   order is bad or not visible to the caller's org — treat both as
 *   "bad/inaccessible order."
 */
export async function getReanalysisCount(
  orderId: string,
  caller: string,
  signal?: AbortSignal
): Promise<ReanalysisCountResponse> {
  return Sentry.startSpan(
    {
      op: 'report.reanalysis_count.get',
      name: 'Get Order Reanalysis Count',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)

      const client = ProviderAPIClient.getInstance()
      const response = await client.get(
        `/orders/${orderId}/reanalysis-count?caller=${encodeURIComponent(caller)}`,
        undefined,
        true,
        signal
      )

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to get reanalysis count: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.reanalysis_count.get', orderId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      return response.json()
    }
  )
}

/**
 * Previews the reanalysis decision for a pending order edit WITHOUT committing it
 * (POST /orders/{order_id}/reanalysis-preview).
 *
 * The SPA reports only the two raw change facts (``clinicalInformationModified``,
 * ``comparatorAdded``); provider-api owns the decision + enforcement and returns
 * the authoritative :interface:`ReanalysisDecision` (would-trigger + blocked
 * reason + warranted). The Send Updates modal calls this when it opens so its
 * copy ("Reanalyze" vs "Send updates", the blocked note) matches what the commit
 * will actually do. The committing PUT returns the same decision shape, which is
 * authoritative if state changed between preview and commit.
 *
 * Non-mutating and idempotent — safe to re-call as the user toggles edits.
 *
 * @param orderId - The order ID (UUID)
 * @param signals - The raw change facts (clinical modified / comparator added)
 * @param caller - The caller identifier (e.g. 'spa')
 * @param signal - Optional AbortSignal to cancel a superseded preview
 * @returns Promise with the reanalysis decision payload
 * @throws Error if the request fails (401/403/404/422/500). A 404/422 means the
 *   order is bad or not visible to the caller's org. Callers should fail safe
 *   (leave the control enabled) rather than hard-block on a failed preview.
 */
export async function getReanalysisPreview(
  orderId: string,
  signals: ReanalysisSignals,
  caller: string,
  signal?: AbortSignal
): Promise<ReanalysisDecision> {
  return Sentry.startSpan(
    {
      op: 'report.reanalysis_preview.post',
      name: 'Preview Order Reanalysis Decision',
    },
    async (span) => {
      span.setAttribute('order.id', orderId)
      span.setAttribute('reanalysis.clinical_modified', signals.clinicalInformationModified)
      span.setAttribute('reanalysis.comparator_added', signals.comparatorAdded)

      const client = ProviderAPIClient.getInstance()
      const response = await client.post(
        `/orders/${orderId}/reanalysis-preview?caller=${encodeURIComponent(caller)}`,
        signals,
        { 'Content-Type': 'application/json' },
        true,
        signal
      )

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(`Failed to preview reanalysis decision: ${response.status} ${response.statusText}`)
        Sentry.captureException(error, {
          tags: { operation: 'report.reanalysis_preview.post', orderId },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      return response.json()
    }
  )
}

/**
 * Resolves the current user's linked practitioner ID via GET /auth/practitioner.
 *
 * @returns Promise with { userId, practitionerId } where practitionerId is null if not linked
 */
export async function getCurrentPractitioner(): Promise<{ userId: string; practitionerId: string | null }> {
  return Sentry.startSpan(
    {
      op: 'auth.practitioner',
      name: 'Get Current User Practitioner',
    },
    async (span) => {
      const client = ProviderAPIClient.getInstance()
      const response = await client.get('/auth/practitioner')

      span.setAttribute('http.status_code', response.status)

      if (!response.ok) {
        span.setAttribute('error', true)
        const error = new Error(
          `Failed to get practitioner for current user: ${response.status} ${response.statusText}`
        )
        Sentry.captureException(error, {
          tags: { operation: 'auth.practitioner' },
          contexts: { response: { status: response.status, statusText: response.statusText } },
        })
        throw error
      }

      return response.json()
    }
  )
}
